/* Checkout button guard — HEllO EDC (India store).
   Added 9 Sep 2026. Rewritten the same day once the real cause was found.

   THE SYMPTOM

   Add to cart, the drawer opens, and the GoKwik checkout button spins forever.
   Navigate to any other page, come back to the cart, and it works.

   WHY

   GoKwik's button is delivered `disabled` with class `disabled`, and
   snippets/gokwik.liquid styles the button's loader to show whenever that
   class is present — so "spinning forever" means, precisely, "nobody ever
   removed .disabled from this button".

   Two things are supposed to remove it, both inside snippets/gokwik.liquid:

     1. A one-shot handler on GoKwik's `gokwikLoaded` event. This fires once,
        early, and enables whatever buttons exist at that moment. It is why a
        fresh page load works.

     2. A MutationObserver on <body> meant to catch buttons that appear later
        — which is exactly what happens when Dawn re-renders the cart drawer
        after an add-to-cart, replacing the GoKwik button with a fresh,
        disabled one.

   (2) never fires on this store. Its callback is wrapped in a 700 ms debounce,
   and every mutation anywhere in <body> resets that timer. This storefront
   runs a lot of apps — Judge.me, Nector, Foxkit, Easify, Rapid Search,
   Instafeed, Return Prime — all mutating the DOM more often than every 700 ms.
   The debounce is starved and its callback is never reached. A page navigation
   works only because it re-runs path (1), which is not debounced.

   THE FIX

   Do GoKwik's own re-enable, but on a schedule that cannot be starved: act on
   the leading edge of DOM changes AND on a plain low-frequency interval that
   is independent of mutation traffic. The work is a querySelectorAll over one
   or two elements, so running it twice a second costs nothing.

   This never enables a button before GoKwik has signalled it is ready, so it
   cannot put a shopper into a checkout that is not up. It only re-applies an
   enable GoKwik already granted and then lost.

   SECOND JOB — if GoKwik never comes up at all

   merchant.integration.js failing to load would leave the native Shopify
   button hidden (gokwik.liquid hides it for India visitors) and GoKwik's dead:
   no way to pay, on a cart that looks fine. After a deadline this puts the
   native button back. Worst case both buttons show — cosmetic, not a lost sale.

   Worth raising with GoKwik: their post-render re-enable does not survive a
   Dawn cart-drawer section re-render on a DOM-busy storefront. This file is a
   workaround, not a repair. */

(function () {
  'use strict';

  if (window.__wcCheckoutGuard) return;
  window.__wcCheckoutGuard = true;

  var SWEEP_MS = 500;       /* unconditional heartbeat — cannot be starved */
  var DEADLINE_MS = 8000;   /* how long GoKwik gets to come up */
  var STYLE_ID = 'wc-checkout-guard-style';

  var ready = false;        /* sticky once GoKwik has signalled */
  var started = Date.now();
  var fellBack = false;

  function buttons() {
    return document.querySelectorAll('.gokwik-checkout button, #gokwik-buy-now');
  }

  function isLive(btn) {
    return !btn.disabled && !btn.classList.contains('disabled');
  }

  /* GoKwik counts as ready if it told us, if we can see a button it already
     enabled, or if its integration script has finished loading. The last two
     cover the case where `gokwikLoaded` fired before this file was parsed —
     GoKwik's script sits in <head> and ours at the end of <body>. */
  function detectReady() {
    if (ready) return true;

    var list = buttons();
    for (var i = 0; i < list.length; i++) {
      if (isLive(list[i])) { ready = true; return true; }
    }

    try {
      var entries = performance.getEntriesByType('resource');
      for (var j = 0; j < entries.length; j++) {
        var n = entries[j].name || '';
        if (n.indexOf('merchant.integration.js') > -1 && entries[j].responseEnd > 0) {
          ready = true;
          return true;
        }
      }
    } catch (e) { /* resource timing unavailable — fall through */ }

    return false;
  }

  function enableAll() {
    var changed = 0;
    var list = buttons();
    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      if (isLive(b)) continue;
      /* #gokwik-buy-now is deliberately disabled while the product form itself
         is unbuyable — leave that case alone. */
      if (b.id === 'gokwik-buy-now') {
        var add = document.querySelector("button[name='add']");
        if (add && add.disabled) continue;
      }
      b.disabled = false;
      b.classList.remove('disabled');
      changed++;
    }
    return changed;
  }

  function restoreNativeButton(reason) {
    if (fellBack || document.getElementById(STYLE_ID)) return;
    fellBack = true;
    try {
      var s = document.createElement('style');
      s.id = STYLE_ID;
      /* Beat the !important rule injected by snippets/gokwik.liquid. */
      s.textContent = '.shopifycheckoutbtn{display:block !important;}';
      document.head.appendChild(s);
      if (window.console && console.warn) {
        console.warn('[WC] checkout guard: restored native Shopify checkout button (' + reason + ')');
      }
    } catch (e) { /* no-op */ }
  }

  function sweep() {
    try {
      if (detectReady()) {
        enableAll();
        return;
      }
      if (fellBack) return;
      if (Date.now() - started < DEADLINE_MS) return;
      restoreNativeButton(document.querySelector('.gokwik-checkout')
        ? 'GoKwik button never enabled'
        : 'GoKwik button never rendered');
    } catch (e) { /* leave the page exactly as found */ }
  }

  /* 1. The heartbeat. Independent of mutation traffic, so nothing can starve
        it the way GoKwik's debounce is starved. */
  setInterval(sweep, SWEEP_MS);

  /* 2. Leading-edge reaction, so the button is live before the drawer's open
        animation finishes rather than up to half a second later. Deliberately
        NOT debounced. childList only, so our own attribute edits cannot feed
        it back into itself. */
  try {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes && records[i].addedNodes.length) { sweep(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  } catch (e) { /* no-op */ }

  /* 3. Direct signals. */
  window.addEventListener('gokwikLoaded', function () { ready = true; sweep(); });
  ['cart:refresh', 'cart:updated', 'wc:cart-synced'].forEach(function (evt) {
    document.addEventListener(evt, sweep);
  });

  sweep();
})();
